import { useInfiniteQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../api.js';
import { Button } from '../components/ui.js';

interface AuditEntry {
  id: number;
  at: string;
  actor: string;
  action: string;
  entity: string;
  entityId: string | null;
  before: unknown;
  after: unknown;
}

export function AuditPage() {
  const [entity, setEntity] = useState('');
  const [action, setAction] = useState('');
  const query = useInfiniteQuery({
    queryKey: ['audit', entity, action],
    initialPageParam: null as number | null,
    queryFn: ({ pageParam }) => {
      const params = new URLSearchParams({ limit: '100' });
      if (entity) params.set('entity', entity);
      if (action) params.set('action', action);
      if (pageParam) params.set('beforeId', String(pageParam));
      return api<{ items: AuditEntry[]; nextBeforeId: number | null }>('GET', `/api/audit?${params}`);
    },
    getNextPageParam: (last) => last.nextBeforeId,
  });
  const rows = query.data?.pages.flatMap((p) => p.items) ?? [];
  return (
    <section className="space-y-4">
      <h1 className="text-xl font-semibold">Audit log</h1>
      <div className="flex gap-3 text-sm">
        <input className="rounded border border-slate-300 px-2 py-1 dark:border-slate-700 dark:bg-slate-900" placeholder="entity (e.g. users)" value={entity} onChange={(e) => setEntity(e.target.value.trim())} />
        <input className="rounded border border-slate-300 px-2 py-1 dark:border-slate-700 dark:bg-slate-900" placeholder="action (insert, update, login ...)" value={action} onChange={(e) => setAction(e.target.value.trim())} />
      </div>
      <table className="w-full text-left text-sm">
        <thead className="text-slate-500">
          <tr>
            <th className="py-1">Time</th>
            <th>Actor</th>
            <th>Action</th>
            <th>Entity</th>
            <th>Change</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-t border-slate-200 align-top dark:border-slate-800">
              <td className="whitespace-nowrap py-1.5 pr-3">{new Date(r.at).toLocaleString('en-GB')}</td>
              <td className="pr-3">{r.actor}</td>
              <td className="pr-3">{r.action}</td>
              <td className="pr-3">
                {r.entity}
                {r.entityId && <span className="block font-mono text-xs text-slate-500">{r.entityId}</span>}
              </td>
              <td>
                <details>
                  <summary className="cursor-pointer text-slate-500">details</summary>
                  <pre className="max-w-xl overflow-auto text-xs">{JSON.stringify({ before: r.before, after: r.after }, null, 2)}</pre>
                </details>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {query.hasNextPage && (
        <Button onClick={() => query.fetchNextPage()} disabled={query.isFetchingNextPage}>
          Load more
        </Button>
      )}
    </section>
  );
}
